import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, Plus, X, Edit2 } from 'lucide-react';
import { NotebookEntry } from '../../types/story';

interface NotebookProps {
  entries: NotebookEntry[];
  onAddEntry: (content: string) => void;
}

export const Notebook: React.FC<NotebookProps> = ({ entries, onAddEntry }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddNote = () => {
    if (newNote.trim()) {
      onAddEntry(newNote.trim());
      setNewNote('');
      setIsAdding(false);
    }
  };

  return (
    <>
      {/* 笔记本按钮 */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-amber-400 to-amber-600 text-white rounded-full shadow-lg flex items-center justify-center"
      >
        <Book className="w-6 h-6" />
      </motion.button>

      {/* 笔记本面板 */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* 遮罩 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
            />

            {/* 笔记本内容 */}
            <motion.div
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 overflow-hidden flex flex-col"
            >
              {/* 标题栏 */}
              <div className="bg-gradient-to-r from-amber-400 to-amber-600 text-white p-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold flex items-center">
                    <Book className="w-6 h-6 mr-2" />
                    暗数笔记本
                  </h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* 笔记列表 */}
              <div className="flex-1 overflow-y-auto p-4">
                {entries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">还没有任何笔记</p>
                ) : (
                  <div className="space-y-3">
                    {entries.map((entry, index) => (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-amber-50 rounded-lg p-3 border border-amber-200"
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-semibold text-amber-900">{entry.title}</h4>
                          <span className="text-xs text-amber-600">{entry.date}</span>
                        </div>
                        <p className="text-sm text-gray-700">{entry.content}</p>
                        {entry.tags.length > 0 && (
                          <div className="mt-2 flex gap-1">
                            {entry.tags.map(tag => (
                              <span
                                key={tag}
                                className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* 添加笔记 */}
              <div className="border-t p-4">
                {!isAdding ? (
                  <button
                    onClick={() => setIsAdding(true)}
                    className="w-full py-2 border-2 border-dashed border-amber-300 rounded-lg text-amber-600 hover:bg-amber-50 transition flex items-center justify-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    添加笔记
                  </button>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="写下你的想法..."
                      className="w-full p-2 border rounded-lg resize-none h-24 focus:outline-none focus:border-amber-400"
                      autoFocus
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:bg-gray-300"
                      >
                        保存
                      </button>
                      <button
                        onClick={() => {
                          setIsAdding(false);
                          setNewNote('');
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};